import { TRUCK_PHYSICS, type TruckMotion } from "@townsy/world";

/**
 * The truck the local visitor drives, as predicted (TruckDriver adds every simulation step; the arena's 3D reads it
 * every frame). One town session per page, so one local truck.
 *
 * ADR-0032 (the founder's "very jumpy and glitchy" truck): each step is kept with the time it STANDS FOR on an even
 * 50 ms timeline, not the time the browser happened to compute it. The movement loop runs on a timer that often fires
 * late and then runs two steps at once; stamping both with "now" made the drawn truck skip a whole step (0.85 m at top
 * speed) and then stand still until the next late timer. The renderer samples this timeline one step in the past and
 * only extrapolates a few milliseconds when a timer is late, so the drawn truck moves evenly however the timer fires.
 * Server states never touch this timeline (the prediction's smoothed correction keeps the drawn pose continuous); only
 * a large correction (a teleport onto the race grid) starts it over.
 */
export interface LocalTruckSample {
  vehicleId: string;
  /** The newest predicted pose (the HUD's speed). */
  current: TruckMotion;
  /** Timeline time (local ms) of the newest step. */
  at: number;
  /** Step length (ms). */
  stepMs: number;
}

interface Entry {
  t: number;
  m: TruckMotion;
}

/** Steps kept (a little over half a second at 20 Hz). */
const HISTORY = 12;
/** A step whose timeline time falls this many steps behind the clock starts the timeline over (a stalled tab). */
const RESYNC_STEPS = 3;
/** The drawn pose may run ahead of the newest step by at most this many steps (a late timer). */
const MAX_EXTRAPOLATE_STEPS = 1;

let vehicle: string | null = null;
let stepMs = 50;
let history: Entry[] = [];

export const localTruck = {
  /**
   * A new predicted step computed at local time `now`. Its timeline time is the previous step's plus one step, never
   * later than `now` (a step is never computed before its time) and restarted at `now` after a stall.
   */
  push(vehicleId: string, motion: TruckMotion, now: number, step: number): void {
    if (vehicle !== vehicleId || history.length === 0) {
      localTruck.reset(vehicleId, motion, now, step);
      return;
    }
    stepMs = step;
    const last = history[history.length - 1]!;
    let t = last.t + step;
    if (t > now) t = now;
    if (t < now - RESYNC_STEPS * step) t = now;
    if (t < last.t) t = last.t;
    history.push({ t, m: motion });
    if (history.length > HISTORY) history.splice(0, history.length - HISTORY);
  },
  /** Start the timeline over at this pose, drawn at once (first step, a snap correction, a new truck). */
  reset(vehicleId: string, motion: TruckMotion, now: number, step: number): void {
    vehicle = vehicleId;
    stepMs = step;
    history = [{ t: now, m: motion }];
  },
  get(): LocalTruckSample | null {
    const last = history[history.length - 1];
    return vehicle && last ? { vehicleId: vehicle, current: last.m, at: last.t, stepMs } : null;
  },
  clear(vehicleId?: string): void {
    if (!vehicleId || vehicle === vehicleId) {
      vehicle = null;
      history = [];
    }
  },
};

function lerpAngle(a: number, b: number, t: number): number {
  const d = Math.atan2(Math.sin(b - a), Math.cos(b - a));
  return a + d * t;
}

/** The local truck's pose to draw at local time `now`: the predicted timeline one step in the past. */
export function localTruckPose(now: number): (TruckMotion & { vehicleId: string }) | null {
  if (!vehicle || history.length === 0) return null;
  const at = now - stepMs;
  const first = history[0]!;
  if (at <= first.t) return { vehicleId: vehicle, ...first.m };
  for (let i = 1; i < history.length; i++) {
    const b = history[i]!;
    if (b.t < at) continue;
    const a = history[i - 1]!;
    const f = b.t > a.t ? (at - a.t) / (b.t - a.t) : 1;
    return {
      vehicleId: vehicle,
      x: a.m.x + (b.m.x - a.m.x) * f,
      z: a.m.z + (b.m.z - a.m.z) * f,
      y: a.m.y + (b.m.y - a.m.y) * f,
      heading: lerpAngle(a.m.heading, b.m.heading, f),
      speed: b.m.speed,
      vy: b.m.vy,
      airborne: b.m.airborne,
    };
  }
  // A late timer: carry the newest step on along its heading for a moment (never more than a step).
  const last = history[history.length - 1]!;
  const ahead = Math.min(at - last.t, MAX_EXTRAPOLATE_STEPS * stepMs) / 1000;
  const m = last.m;
  const y = m.airborne ? Math.max(0, m.y + m.vy * ahead - 0.5 * TRUCK_PHYSICS.gravity * ahead * ahead) : m.y;
  return { vehicleId: vehicle, ...m, x: m.x + Math.sin(m.heading) * m.speed * ahead, z: m.z + Math.cos(m.heading) * m.speed * ahead, y };
}
